// Fiche d'un projet de travaux (M14, lot L4-C, D-146) : objet, financement, contrats liés, avancements
// physique et financier côte à côte, jalons, situations, rapports, réceptions et réserves, clôture. Les
// actes dépendent de l'état du projet et des droits ; le serveur refait tous les contrôles.
import { Bouton, Lien, PuceEtat } from '@cc/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useContrats } from '../achats/api';
import { cloturerProjet, INVALIDER_TRAVAUX, lancerProjet, revenirAuCalcul, useProjet } from './api';
import { FormulaireAvancement, FormulaireFinancement, FormulaireJalons, FormulaireLevee, FormulaireLiaisonContrat, FormulaireRapport, FormulaireReception, FormulaireSituation } from './FormulairesTravaux';
import { actesProjet, francs, libelleProjet, TON_PROJET } from './logique';
import { Avancements, SectionJalons, SectionRapports, SectionReceptions, SectionSituations } from './SectionsProjet';
import { SOURCES_FINANCEMENT } from '@cc/contrats';
import styles from '../achats/achats.module.css';

type Dialogue = 'financement' | 'contrat' | 'jalons' | 'avancement' | 'situation' | 'rapport' | 'provisoire' | 'definitive' | { levee: string; description: string } | null;

export function PageProjet() {
  const { id } = useParams();
  const { contexte } = useSession();
  const projet = useProjet(id);
  const voitAchats = peut(contexte, 'achat', 'voir');
  const contrats = useContrats();
  const [d, setD] = useState<Dialogue>(null);
  const droits = { modifier: peut(contexte, 'travaux', 'modifier'), valider: peut(contexte, 'travaux', 'valider'), signer: peut(contexte, 'travaux', 'signer'), decider: peut(contexte, 'travaux', 'decider'), liquider: peut(contexte, 'depense', 'modifier') };
  const lancer = useAction({ executer: (pid: string) => lancerProjet(pid), succes: 'Projet lancé.', invalider: INVALIDER_TRAVAUX });
  const cloturer = useAction({ executer: (pid: string) => cloturerProjet(pid), succes: 'Projet clos.', invalider: INVALIDER_TRAVAUX });
  const calcul = useAction({ executer: (pid: string) => revenirAuCalcul(pid), succes: 'Avancement physique recalculé sur les jalons.', invalider: INVALIDER_TRAVAUX });
  const p = projet.data;
  const actes = p ? actesProjet(p, droits) : [];
  const erreur = lancer.messageErreur ?? cloturer.messageErreur ?? calcul.messageErreur;
  return (
    <>
      <EnTetePage
        etiquette="Travaux et chantiers"
        titre={p ? `${p.numero} : ${p.intitule}` : 'Projet de travaux'}
        description={p ? `${p.adresse ?? 'Lieu non renseigné'}, du ${p.debut_prevu} au ${p.fin_prevue}.` : undefined}
        actions={<Lien href="/travaux">Tous les projets</Lien>}
      />
      <EtatRequete chargement={projet.isPending} erreur={projet.error} onReessayer={() => void projet.refetch()} lot="L4-C" fonction="La fiche du projet">
        {p ? (
          <>
            <div className={styles.actes}>
              <PuceEtat ton={TON_PROJET[p.etat]} libelle={libelleProjet(p.etat)} />
              {p.en_retard ? <PuceEtat ton="retard" libelle="En retard" /> : null}
              {actes.includes('jalons') ? <Bouton variante="secondaire" onClick={() => setD('jalons')}>Définir les jalons</Bouton> : null}
              {actes.includes('lancer') ? <Bouton variante="principal" chargement={lancer.enCours} onClick={() => lancer.lancer(p.id)}>Lancer le chantier</Bouton> : null}
              {actes.includes('avancement') ? <Bouton variante="secondaire" onClick={() => setD('avancement')}>Saisir l&apos;avancement</Bouton> : null}
              {actes.includes('avancement') && p.avancement_physique.mode === 'saisi' ? <Bouton variante="discret" chargement={calcul.enCours} onClick={() => calcul.lancer(p.id)}>Revenir au calcul</Bouton> : null}
              {actes.includes('situation') ? <Bouton variante="secondaire" onClick={() => setD('situation')}>Situation de travaux</Bouton> : null}
              {p.etat !== 'clos' && peut(contexte, 'travaux', 'creer') ? <Bouton variante="secondaire" onClick={() => setD('rapport')}>Rapport de chantier</Bouton> : null}
              {actes.includes('reception_provisoire') ? <Bouton variante="principal" onClick={() => setD('provisoire')}>Réception provisoire</Bouton> : null}
              {actes.includes('reception_definitive') ? <Bouton variante="principal" onClick={() => setD('definitive')}>Réception définitive</Bouton> : null}
              {actes.includes('cloturer') ? <Bouton variante="principal" chargement={cloturer.enCours} onClick={() => cloturer.lancer(p.id)}>Clore le projet</Bouton> : null}
            </div>
            {erreur ? <p role="alert" className={styles.negatif}>{erreur}</p> : null}
            <Avancements p={p} />
            <section className={styles.bloc} aria-labelledby="titre-projet">
              <h2 id="titre-projet">Projet</h2>
              <dl className={styles.fiche}>
                <dt>Objet</dt><dd>{p.objet}</dd>
                <dt>Service</dt><dd>{p.service?.nom ?? 'Non renseigné'}</dd>
                <dt>Financement</dt>
                <dd>
                  {p.financements.length === 0 ? 'Aucune source' : p.financements.map((f) => `${SOURCES_FINANCEMENT[f.source]} : ${f.libelle}, ${francs(f.montant, p.fictif)}${f.ligne ? ` (ligne ${f.ligne.code})` : ''}`).join(' ; ')}
                  {p.financements.length > 0 ? ` ; total ${francs(p.total_financement, p.fictif)}` : ''}
                  {droits.modifier && p.etat !== 'clos' ? <> <Bouton variante="discret" onClick={() => setD('financement')}>Ajouter</Bouton></> : null}
                </dd>
                <dt>Contrats</dt>
                <dd>
                  {p.contrats.length === 0 ? 'Aucun contrat lié' : p.contrats.map((c) => `${c.numero} (${c.fournisseur}, ${francs(c.montant_actuel, p.fictif)})`).join(' ; ')}
                  {droits.modifier && voitAchats && p.etat !== 'clos' ? <> <Bouton variante="discret" onClick={() => setD('contrat')}>Lier un contrat</Bouton></> : null}
                </dd>
              </dl>
            </section>
            <SectionJalons p={p} modifier={droits.modifier} valider={droits.valider} />
            <SectionSituations p={p} />
            <SectionRapports p={p} />
            <SectionReceptions p={p} valider={droits.valider} onLever={(rid, description) => setD({ levee: rid, description })} />
            {d === 'financement' ? <FormulaireFinancement projet={p} onFermer={() => setD(null)} /> : null}
            {d === 'contrat' ? <FormulaireLiaisonContrat projet={p} contrats={contrats.data?.elements ?? []} onFermer={() => setD(null)} /> : null}
            {d === 'jalons' ? <FormulaireJalons projet={p} onFermer={() => setD(null)} /> : null}
            {d === 'avancement' ? <FormulaireAvancement projet={p} onFermer={() => setD(null)} /> : null}
            {d === 'situation' ? <FormulaireSituation projet={p} onFermer={() => setD(null)} /> : null}
            {d === 'rapport' ? <FormulaireRapport projet={p} onFermer={() => setD(null)} /> : null}
            {d === 'provisoire' || d === 'definitive' ? <FormulaireReception projet={p} type={d} onFermer={() => setD(null)} /> : null}
            {d && typeof d === 'object' ? <FormulaireLevee reserveId={d.levee} description={d.description} onFermer={() => setD(null)} /> : null}
          </>
        ) : null}
      </EtatRequete>
    </>
  );
}
